import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api";

interface FamilyDetail {
  family: {
    id: string;
    clinicId: string;
    city: string | null;
    pincode: string | null;
    source: string;
    flagged: number;
    flagReason: string | null;
    createdAt: string;
  };
  children: Array<{
    id: string;
    fullName: string;
    dob: string;
    gender: string | null;
    grade: string | null;
  }>;
  parents: Array<{
    id: string;
    fullName: string;
    relationship: string;
    phone: string;
    email: string | null;
    isPrimary: number;
    whatsappOptIn: number;
  }>;
  conversations: Array<{
    id: string;
    flow: string;
    startedAt: string;
    endedAt: string | null;
    beatsCompleted: number | null;
    outcome: string | null;
    recommendedTier: string | null;
  }>;
  subscriptions: Array<{
    id: string;
    tier: string;
    status: string;
    annualPriceInr: number;
    startsAt: string;
    expiresAt: string;
  }>;
  screenings: Array<{
    id: string;
    childId: string;
    visitType: string;
    visitDate: string;
    phrPdfUrl: string | null;
  }>;
  findings: Array<{
    id: string;
    screeningId: string;
    category: string;
    findingLabel: string;
    severity: "normal" | "mild" | "moderate" | "severe" | "critical";
    flaggedForFollowup: number;
    createdAt: string;
  }>;
  payments: Array<{
    id: string;
    razorpayEvent: string;
    amountInrPaise: number;
    status: string;
    occurredAt: string;
  }>;
  whatsapp: Array<{
    id: string;
    direction: "inbound" | "outbound";
    templateName: string | null;
    body: string | null;
    status: string;
    occurredAt: string;
  }>;
}

interface TimelineEvent {
  at: string;
  kind:
    | "screening"
    | "finding_severe"
    | "conversation"
    | "subscription"
    | "payment"
    | "wa_out"
    | "wa_in"
    | "flag";
  title: string;
  detail?: string;
  link?: { to: string; params: Record<string, string> };
  tone?: "alert" | "warn" | "ok" | "neutral";
}

function buildTimeline(d: FamilyDetail): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  for (const s of d.screenings) {
    const child = d.children.find((c) => c.id === s.childId);
    events.push({
      at: s.visitDate,
      kind: "screening",
      title: `${s.visitType} screening for ${child?.fullName ?? "child"}`,
      detail: s.phrPdfUrl ? "PHR generated" : undefined,
    });
  }
  for (const f of d.findings) {
    if (f.severity === "severe" || f.severity === "critical") {
      events.push({
        at: f.createdAt,
        kind: "finding_severe",
        title: `${f.severity.toUpperCase()} finding: ${f.findingLabel}`,
        detail: f.category,
        tone: "alert",
      });
    }
  }
  for (const c of d.conversations) {
    events.push({
      at: c.startedAt,
      kind: "conversation",
      title: `${c.flow} conversation`,
      detail:
        c.outcome && c.recommendedTier
          ? `${c.outcome} · tier ${c.recommendedTier}`
          : c.outcome ?? undefined,
      tone: c.outcome === "subscribed" ? "ok" : c.outcome === "not_interested" ? "warn" : "neutral",
    });
  }
  for (const s of d.subscriptions) {
    events.push({
      at: s.startsAt,
      kind: "subscription",
      title: `${s.tier} subscription · ₹${s.annualPriceInr}`,
      detail: `status: ${s.status}`,
      link: { to: "/subscriptions/$subscriptionId", params: { subscriptionId: s.id } },
      tone: s.status === "active" ? "ok" : s.status === "lapsed" ? "alert" : "warn",
    });
  }
  for (const p of d.payments) {
    events.push({
      at: p.occurredAt,
      kind: "payment",
      title: `${p.razorpayEvent} ₹${(p.amountInrPaise / 100).toLocaleString("en-IN")}`,
      detail: p.status,
      tone: p.status === "captured" ? "ok" : p.status === "failed" ? "alert" : "neutral",
    });
  }
  for (const w of d.whatsapp) {
    events.push({
      at: w.occurredAt,
      kind: w.direction === "inbound" ? "wa_in" : "wa_out",
      title:
        w.direction === "inbound"
          ? `inbound WhatsApp${w.body ? `: ${w.body.slice(0, 60)}` : ""}`
          : `→ template: ${w.templateName ?? "—"}`,
      detail: w.status,
    });
  }
  if (d.family.flagged === 1) {
    events.push({
      at: d.family.createdAt,
      kind: "flag",
      title: "FAMILY FLAGGED",
      detail: d.family.flagReason ?? "no reason recorded",
      tone: "alert",
    });
  }
  events.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  return events;
}

const KIND_ICON: Record<TimelineEvent["kind"], string> = {
  screening: "🩺",
  finding_severe: "🚩",
  conversation: "💬",
  subscription: "📋",
  payment: "💳",
  wa_out: "↗︎",
  wa_in: "↘︎",
  flag: "⚠︎",
};

function Family() {
  const { familyId } = Route.useParams();
  const [data, setData] = useState<FamilyDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [me, setMe] = useState<{ role: string } | null>(null);
  const [tab, setTab] = useState<"timeline" | "details" | "notes">("timeline");
  const [flagOpen, setFlagOpen] = useState(false);

  const load = () =>
    apiFetch<FamilyDetail>(`/api/families/${familyId}`)
      .then(setData)
      .catch((e: Error) => setErr(e.message));

  useEffect(() => {
    load();
    apiFetch<{ role: string }>("/api/me").then(setMe).catch(() => {});
  }, [familyId]);

  const flag = async (flagged: boolean, reason?: string) => {
    await apiFetch(`/api/families/${familyId}/flag`, {
      method: "PATCH",
      body: JSON.stringify({ flagged, reason }),
    });
    setFlagOpen(false);
    load();
  };

  if (err) return <div className="panel text-red-600">{err}</div>;
  if (!data) return <div className="panel text-slate-500">Loading…</div>;

  const canFlag = me?.role === "doctor" || me?.role === "admin";
  const events = buildTimeline(data);

  return (
    <div className="space-y-4">
      <div className={`panel ${data.family.flagged === 1 ? "border-red-300 bg-red-50" : ""}`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide">
              Screen 7
            </div>
            <h1 className="text-2xl font-semibold mb-2">Family Record</h1>
            <div className="text-xs text-slate-500">
              <code>{data.family.id}</code> · created{" "}
              {new Date(data.family.createdAt).toLocaleDateString()} · source{" "}
              <span className="rounded bg-slate-100 px-1.5">
                {data.family.source}
              </span>
              {data.family.city && ` · ${data.family.city}`}
              {data.family.pincode && ` ${data.family.pincode}`}
            </div>
            {data.family.flagged === 1 && (
              <div className="mt-2 text-sm text-red-700">
                ⚠︎ FLAGGED: {data.family.flagReason ?? "no reason recorded"}
              </div>
            )}
          </div>
          {canFlag && (
            <div className="flex flex-col gap-1 items-end">
              {data.family.flagged === 1 ? (
                <button
                  onClick={() => flag(false)}
                  className="text-xs rounded bg-slate-100 text-slate-700 px-2 py-1"
                >
                  Unflag
                </button>
              ) : flagOpen ? (
                <FlagForm onCancel={() => setFlagOpen(false)} onSave={(r) => flag(true, r)} />
              ) : (
                <button
                  onClick={() => setFlagOpen(true)}
                  className="text-xs rounded bg-red-600 text-white px-2 py-1"
                >
                  Flag family
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="panel">
        <div className="flex gap-1 border-b border-slate-200 -mb-px">
          {(["timeline", "details", "notes"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-2 text-sm border-b-2 ${
                tab === t
                  ? "border-slate-900 text-slate-900 font-medium"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t === "timeline"
                ? `Timeline (${events.length})`
                : t === "details"
                  ? "Details"
                  : "Doctor notes"}
            </button>
          ))}
        </div>
      </div>

      {tab === "timeline" ? (
        <Timeline events={events} />
      ) : tab === "details" ? (
        <Details data={data} />
      ) : (
        <DoctorNotesTab familyId={data.family.id} canWrite={me?.role === "doctor" || me?.role === "admin"} />
      )}
    </div>
  );
}

interface DoctorNote {
  id: string;
  body: string;
  authorEmail: string;
  authorRoleSnapshot: string;
  createdAt: string;
}

function DoctorNotesTab({
  familyId,
  canWrite,
}: {
  familyId: string;
  canWrite: boolean;
}) {
  const [notes, setNotes] = useState<DoctorNote[]>([]);
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = () =>
    apiFetch<{ notes: DoctorNote[] }>(`/api/families/${familyId}/doctor-notes`)
      .then((r) => setNotes(r.notes))
      .catch((e: Error) => setErr(e.message));
  useEffect(load, [familyId]);

  const submit = async () => {
    if (draft.trim().length < 3) return;
    setSubmitting(true);
    setErr(null);
    try {
      await apiFetch(`/api/families/${familyId}/doctor-notes`, {
        method: "POST",
        body: JSON.stringify({ body: draft.trim() }),
      });
      setDraft("");
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      {canWrite && (
        <div className="panel">
          <h3 className="text-sm font-medium mb-2">Add clinical note</h3>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            placeholder="Doctor's clinical observations, questions, plan… (append-only; cannot be edited later)"
            className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
          />
          <div className="flex gap-2 items-center mt-2">
            <button
              disabled={submitting || draft.trim().length < 3}
              onClick={submit}
              className="rounded bg-slate-900 text-white px-3 py-1 text-sm disabled:opacity-50"
            >
              {submitting ? "Saving…" : "Add note"}
            </button>
            <span className="text-xs text-slate-500">{draft.length}/5000 chars</span>
          </div>
        </div>
      )}
      {err && <div className="panel text-red-600 text-sm">{err}</div>}
      <div className="panel">
        <h3 className="text-sm font-medium mb-2">
          Notes ({notes.length})
        </h3>
        {notes.length === 0 ? (
          <div className="text-sm text-slate-400">
            No clinical notes recorded yet.
            {!canWrite && " Only doctors and admins can add notes."}
          </div>
        ) : (
          <ul className="space-y-3">
            {notes.map((n) => (
              <li key={n.id} className="border-l-2 border-slate-300 pl-3">
                <div className="text-xs text-slate-500 mb-1">
                  <strong>{n.authorEmail}</strong>{" "}
                  <span className="rounded bg-slate-100 px-1.5">
                    {n.authorRoleSnapshot}
                  </span>{" "}
                  · {new Date(n.createdAt).toLocaleString()}
                </div>
                <div className="text-sm whitespace-pre-wrap">{n.body}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Timeline({ events }: { events: TimelineEvent[] }) {
  if (events.length === 0) {
    return (
      <div className="panel text-sm text-slate-400">
        No events yet for this family.
      </div>
    );
  }
  return (
    <div className="panel">
      <ul className="relative pl-6 border-l-2 border-slate-100 space-y-3">
        {events.map((e, i) => {
          const tone =
            e.tone === "alert"
              ? "border-red-300 bg-red-50"
              : e.tone === "warn"
                ? "border-amber-300 bg-amber-50"
                : e.tone === "ok"
                  ? "border-green-300 bg-green-50"
                  : "border-slate-200 bg-white";
          const Inner = (
            <div className={`rounded border px-3 py-2 text-sm ${tone}`}>
              <div className="flex items-start gap-2">
                <span className="text-base leading-none">{KIND_ICON[e.kind]}</span>
                <div className="flex-1">
                  <div className="font-medium text-slate-900">{e.title}</div>
                  {e.detail && (
                    <div className="text-xs text-slate-500 mt-0.5">{e.detail}</div>
                  )}
                </div>
                <div className="text-xs text-slate-400 whitespace-nowrap">
                  {new Date(e.at).toLocaleString([], {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            </div>
          );
          return (
            <li key={i} className="relative">
              <span className="absolute -left-[27px] top-3 w-3 h-3 rounded-full bg-white border-2 border-slate-300" />
              {e.link ? (
                <Link {...(e.link as never)} className="block">
                  {Inner}
                </Link>
              ) : (
                Inner
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function FlagForm({
  onCancel,
  onSave,
}: {
  onCancel: () => void;
  onSave: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  return (
    <div className="border border-red-300 bg-red-50 rounded p-2 text-xs">
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        placeholder="Reason for flagging…"
        className="w-64 border border-red-200 rounded px-2 py-1"
      />
      <div className="flex gap-1 mt-1">
        <button
          onClick={() => reason.trim() && onSave(reason.trim())}
          className="rounded bg-red-600 text-white px-2 py-1"
        >
          Flag
        </button>
        <button
          onClick={onCancel}
          className="rounded bg-slate-100 text-slate-700 px-2 py-1"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Details({ data }: { data: FamilyDetail }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Section title={`Children (${data.children.length})`}>
          <ul className="space-y-1 text-sm">
            {data.children.map((c) => (
              <li key={c.id}>
                <strong>{c.fullName}</strong>{" "}
                <span className="text-slate-500">
                  · DOB {c.dob}
                  {c.gender && ` · ${c.gender}`}
                  {c.grade && ` · grade ${c.grade}`}
                </span>
              </li>
            ))}
          </ul>
        </Section>
        <Section title={`Parents (${data.parents.length})`}>
          <ul className="space-y-1 text-sm">
            {data.parents.map((p) => (
              <li key={p.id}>
                <strong>{p.fullName}</strong>{" "}
                <span className="text-slate-500">· {p.relationship}</span>{" "}
                <code className="text-xs">{p.phone}</code>
                {p.isPrimary === 1 && (
                  <span className="ml-1 text-xs rounded bg-slate-900 text-white px-1.5">
                    primary
                  </span>
                )}
                {p.whatsappOptIn === 0 && (
                  <span className="ml-1 text-xs rounded bg-amber-100 text-amber-800 px-1.5">
                    WA opted out
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Section>
      </div>
      <Section title={`Subscriptions (${data.subscriptions.length})`}>
        {data.subscriptions.length === 0 ? (
          <div className="text-sm text-slate-500">No subscription yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 text-left">
              <tr>
                <th className="py-1">Tier</th>
                <th>Status</th>
                <th>₹/yr</th>
                <th>Starts</th>
                <th>Expires</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.subscriptions.map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="py-1 capitalize font-medium">{s.tier}</td>
                  <td>
                    <span className="rounded bg-slate-100 px-1.5 text-xs">
                      {s.status}
                    </span>
                  </td>
                  <td>₹{s.annualPriceInr.toLocaleString("en-IN")}</td>
                  <td>{new Date(s.startsAt).toLocaleDateString()}</td>
                  <td>{new Date(s.expiresAt).toLocaleDateString()}</td>
                  <td>
                    <Link
                      to="/subscriptions/$subscriptionId"
                      params={{ subscriptionId: s.id }}
                      className="text-xs text-slate-500 hover:underline"
                    >
                      detail →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="panel">
      <h2 className="font-medium text-sm text-slate-700 mb-2">{title}</h2>
      {children}
    </div>
  );
}

export const Route = createFileRoute("/family/$familyId")({ component: Family });
